import React from "react";
//#region types
import { InvoiceCreateProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { HTMLConstants } from "utils/constants";
//#endregion
//#region components
import {
  PageLayouts,
  ViewPanel,
  SimpleEntityTitle,
  Skeletons,
  DashboardButtonGroup,
  StyledButton,
} from "components/common";
import InvoiceFormContainer from "./forms/create/invoiceFormContainer";
import { Grid } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CreateInvoice: React.FC<InvoiceCreateProps> = (props) => {
  const recurringInvoiceService = useRecurringInvoiceService();
  const { loading, invoice, additionalImages } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const [isRecurring, setIsRecurring] = React.useState(props.isRecurring ?? false);

  const onInvoiceTypeButtonClick = (isRecurring: boolean) => setIsRecurring(isRecurring);

  const reloadInvoice = (invoiceId: number) => {
    if (props.invoice && props.invoice.id) {
      props.reloadInvoice && props.reloadInvoice();
    } else {
      navigate(UrlHelper.formatPath(`/${isRecurring ? "RecurringInvoice" : "Invoice"}/${invoiceId}`), {
        replace: true,
        state: { background: location },
      });
    }
  };

  const upperLeftComponent = React.useMemo(
    () =>
      !isRecurring ? (
        <SimpleEntityTitle
          title={
            (invoice as WintSuperkollAPIModel.Invoice)?.serialNumber
              ? `${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.Invoice2
                )} ${InvoiceHelper.formatInvoiceNumber((invoice as WintSuperkollAPIModel.Invoice).serialNumber!)}`
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.NewInvoice)
          }
          status={LocalizationHelperInstance.getFormattedMessage(
            `Invoice.State_${(invoice as WintSuperkollAPIModel.Invoice)?.status ?? 0}`
          )}
        />
      ) : (
        <SimpleEntityTitle
          title={
            (invoice as WintSuperkollAPIModel.RecurringInvoice)?.recurringDescription
              ? `${(invoice as WintSuperkollAPIModel.RecurringInvoice).recurringDescription}`
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Recurring)
          }
          status={recurringInvoiceService.getStateText(
            (invoice as WintSuperkollAPIModel.RecurringInvoice)?.status ??
              WintSuperkollAPIModel.RecurringInvoiceState.Created
          )}
        />
      ),
    [invoice, isRecurring]
  );

  const upperRightComponent = React.useMemo(
    () =>
      loading ? (
        <></>
      ) : (
        <Grid item container justifyContent="space-between" direction="row" alignItems="center">
          <Grid item id={HTMLConstants.WizardQuickSelectionPortalId}></Grid>
          {props.invoice?.id ? (
            <></>
          ) : (
            <Grid item>
              <DashboardButtonGroup
                buttons={[
                  <StyledButton
                    size="xs"
                    className={isRecurring ? cssExports.transparent : ""}
                    onClick={onInvoiceTypeButtonClick.bind(null, false)}
                  >
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Standard)}
                  </StyledButton>,
                  <StyledButton
                    size="xs"
                    className={isRecurring ? "" : cssExports.transparent}
                    onClick={onInvoiceTypeButtonClick.bind(null, true)}
                  >
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Recurring)}
                  </StyledButton>,
                ]}
                className={cssExports.thin}
              />
            </Grid>
          )}
        </Grid>
      ),
    [loading, isRecurring, props.invoice?.id]
  );

  return (
    <PageLayouts.EntityCreationLayout
      className={cssExports.invoiceCreationWizard}
      upperLeftComponent={upperLeftComponent}
      upperRightComponent={upperRightComponent}
      lowerLeftComponent={<></>}
    >
      {loading ? (
        <ViewPanel>
          <Skeletons.FormSkeleton />
        </ViewPanel>
      ) : (
        <ViewPanel className={cssExports.wizardOuter}>
          <InvoiceFormContainer
            invoice={invoice}
            reloadInvoice={reloadInvoice}
            isRecurring={isRecurring}
            additionalImages={additionalImages}
          />
        </ViewPanel>
      )}
    </PageLayouts.EntityCreationLayout>
  );
};

export default CreateInvoice;
